import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  DisbursementRoundBreakdown,
  DisbursementRoundListParams,
  DisbursementRoundListResponse,
  DisbursementRoundModel,
  DisbursementRoundSendBackPayload,
} from '../../models/disbursement/disbursement-round.model';

// v2.0 phase 2 — รอบส่งเบิก (the ใบปะหน้า round is the approvable). EVERY phase-2
// transition lives on this endpoint; disbursement_cover_memos stays the issue/edit hub.
@Injectable({
  providedIn: 'root',
})
export class DisbursementRoundService {
  private apiUrl = '/disbursement_rounds';

  #http = inject(HttpClient);

  getRounds(params?: DisbursementRoundListParams): Observable<DisbursementRoundListResponse> {
    return this.#http.get<DisbursementRoundListResponse>(this.apiUrl, {
      params: this.buildListParams(params),
    });
  }

  getRound(id: number): Observable<DisbursementRoundModel> {
    return this.#http.get<DisbursementRoundModel>(`${this.apiUrl}/${id}`);
  }

  // เจ้าหน้าที่สารบรรณ: draft|returned_to_officer -> pending_head.
  submit(id: number): Observable<DisbursementRoundModel> {
    return this.#http.post<DisbursementRoundModel>(`${this.apiUrl}/${id}/submit`, {});
  }

  financeSign(id: number): Observable<DisbursementRoundModel> {
    return this.#http.post<DisbursementRoundModel>(`${this.apiUrl}/${id}/finance_sign`, {});
  }

  headCertify(id: number): Observable<DisbursementRoundModel> {
    return this.#http.post<DisbursementRoundModel>(`${this.apiUrl}/${id}/head_certify`, {});
  }

  // Terminal: completes the round, moves every member cert to completed, and re-renders
  // the ใบปะหน้า + round_merged so the Export bundle carries this signature.
  viceDeanSign(id: number): Observable<DisbursementRoundModel> {
    return this.#http.post<DisbursementRoundModel>(`${this.apiUrl}/${id}/vice_dean_sign`, {});
  }

  // The single backward edge — การเงิน only, comment required.
  sendBack(id: number, payload: DisbursementRoundSendBackPayload): Observable<DisbursementRoundModel> {
    return this.#http.post<DisbursementRoundModel>(`${this.apiUrl}/${id}/send_back`, payload);
  }

  // The round's review artifact — one PDF holding the ใบปะหน้า and every member's documents.
  downloadMerged(id: number): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/merged`, { responseType: 'blob' });
  }

  // ยอดของรอบแจกแจงรายอาจารย์ ให้ modal Export โชว์เหนือ preview PDF — แยกจาก getRounds
  // โดยตั้งใจ: round_json คือ body ของทุก transition และของ list ทั้งหน้า
  getBreakdown(id: number): Observable<DisbursementRoundBreakdown> {
    return this.#http.get<DisbursementRoundBreakdown>(`${this.apiUrl}/${id}/breakdown`);
  }

  // v2.0 — the ONE download of the whole module: the completed round's merged PDF.
  // 422 (a Blob body, read it with extractBlobErrorMessage) while the post-signature
  // re-render is still in flight.
  exportRound(id: number): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/export`, { responseType: 'blob' });
  }

  downloadCoverMemo(id: number): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/cover_memo`, { responseType: 'blob' });
  }

  private buildListParams(params?: DisbursementRoundListParams): HttpParams {
    let httpParams = new HttpParams();
    const supportedParams: (keyof DisbursementRoundListParams)[] = [
      'academic_term_id',
      'department_id',
      'path',
      'status',
      'keywords',
      'page',
      'limit',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }
}
